import { useState, useEffect } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { sendCommissionerReminder, updateGroupSettings } from '@/lib/supabase-queries';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MemberBetStatusList } from '@/components/MemberBetStatusList';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface CommissionerTabProps {
  groupId: string;
  seriesList: any[];
  groupName: string;
  paymentLink: string | null;
  chatLink: string | null;
  onGroupUpdated: () => void;
}

const CommissionerTab = ({ groupId, seriesList, groupName, paymentLink, chatLink, onGroupUpdated }: CommissionerTabProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [reminderCooldown, setReminderCooldown] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [editName, setEditName] = useState(groupName);
  const [editPaymentLink, setEditPaymentLink] = useState(paymentLink || '');
  const [editChatLink, setEditChatLink] = useState(chatLink || '');
  const [savingSettings, setSavingSettings] = useState(false);
  const [proModalOpen, setProModalOpen] = useState(false);
  const [inviteRestricted, setInviteRestricted] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const groupData = await supabase.from('groups').select('settings').eq('id', groupId).single();
      const settings = (groupData.data?.settings as Record<string, unknown>) || {};
      setInviteRestricted(settings.invite_restricted === true);
      setLoading(false);
    };
    load();
  }, [groupId]);

  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const result = await sendCommissionerReminder(groupId);
      if (result.skipped) {
        toast({ title: 'התזכורת כבר נשלחה', description: 'ניתן לשלוח תזכורת אחת בשעה' });
      } else {
        toast({ title: 'התזכורת נשלחה בהצלחה' });
      }
    } catch (err: any) {
      toast({ title: 'שגיאה בשליחת תזכורת', description: err.message, variant: 'destructive' });
    }
    setSendingReminder(false);
    // 5-second cooldown to prevent double-tap
    setReminderCooldown(true);
    setTimeout(() => setReminderCooldown(false), 5000);
  };

  const handleSaveSettings = async () => {
    const trimmedName = editName.trim();
    if (trimmedName.length < 2 || trimmedName.length > 50) {
      toast({ title: 'שם הקבוצה חייב להכיל בין 2 ל-50 תווים', variant: 'destructive' });
      return;
    }
    setSavingSettings(true);
    try {
      await updateGroupSettings(groupId, {
        name: trimmedName,
        payment_link: editPaymentLink.trim() || undefined,
        chat_link: editChatLink.trim() || undefined,
        invite_restricted: inviteRestricted,
      });
      toast({ title: 'שם הקבוצה עודכן' });
      onGroupUpdated();
    } catch (err: any) {
      toast({ title: 'שגיאה בשמירת ההגדרות', description: err.message, variant: 'destructive' });
    }
    setSavingSettings(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PRO badge */}
      <div className="flex justify-end">
        <button
          onClick={() => setProModalOpen(true)}
          className="inline-flex items-center gap-1.5 text-amber-400 border border-amber-400/30 bg-amber-400/10 rounded-full px-3 py-1 text-sm font-medium hover:bg-amber-400/20 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
          PRO
        </button>
      </div>

      {/* Member list -- shared component */}
      <MemberBetStatusList groupId={groupId} seriesList={seriesList} />

      {/* Group settings section */}
      <div className="border-t border-border pt-6">
        <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">הגדרות קבוצה</h3>
        <div className="bg-card rounded-2xl border border-border p-4 space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">שם הקבוצה</label>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={50}
              dir="rtl"
              className="bg-background"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">קישור לתשלום</label>
            <Input
              value={editPaymentLink}
              onChange={(e) => setEditPaymentLink(e.target.value)}
              placeholder="https://paybox.me/..."
              dir="ltr"
              className="bg-background"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">קישור לצ'אט קבוצתי</label>
            <Input
              value={editChatLink}
              onChange={(e) => setEditChatLink(e.target.value)}
              placeholder="https://chat.whatsapp.com/..."
              dir="ltr"
              className="bg-background"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">הרשאות הזמנה</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setInviteRestricted(false)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !inviteRestricted
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                פתוח לכולם
              </button>
              <button
                type="button"
                onClick={() => setInviteRestricted(true)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  inviteRestricted
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                קומישינר בלבד
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              size="sm"
              className="gap-1.5"
            >
              {savingSettings && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              שמור
            </Button>
          </div>
        </div>
      </div>

      {/* PRO placeholder modal */}
      <Dialog open={proModalOpen} onOpenChange={setProModalOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              בקרוב — גרסת PRO
            </DialogTitle>
            <DialogDescription>
              שדרוג עתידי יכלול: סייד בטס מותאמים אישית, סטטיסטיקות מתקדמות, ועוד. הישארו מעודכנים!
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProModalOpen(false)}>
              סגור
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionerTab;
